import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Phone, PhoneOff } from 'lucide-react';
import { MessengerAvatar } from './messengerUtils';

/**
 * WhatsApp-style heads-up banner shown INSIDE the app while a call is
 * ringing — the native OS already presents the lock-screen call UI, so
 * foregrounding the full-screen in-app overlay on top of what the user
 * is doing is intrusive. This banner slides down from the top with
 * Decline / Answer actions.
 */
export const IncomingCallHeadsUp = ({
  open,
  contactName,
  contactImage,
  onAccept,
  onDecline,
  dataTestId = 'incoming-call-heads-up',
}) => (
  <AnimatePresence>
    {open ? (
      <motion.div
        key="incoming-heads-up"
        data-testid={dataTestId}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -80, opacity: 0 }}
        transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'fixed',
          top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
          left: 10,
          right: 10,
          zIndex: 13200,
          padding: '10px 10px 10px 12px',
          borderRadius: 20,
          background: 'linear-gradient(135deg, rgba(27,18,48,0.96) 0%, rgba(21,15,34,0.96) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 22px 48px rgba(10,6,24,0.5)',
          color: '#FFFFFF',
          fontFamily: "'Inria Serif', serif",
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <MessengerAvatar
            name={contactName}
            imageUrl={contactImage}
            size={44}
            textSize={16}
            dataTestId={`${dataTestId}-avatar`}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              data-testid={`${dataTestId}-name`}
              style={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {contactName}
            </div>
            <div style={{ fontSize: 12, opacity: 0.82 }}>Allure · Incoming voice call</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
          <PillButton
            data-testid={`${dataTestId}-decline`}
            onClick={onDecline}
            label="Decline"
            icon={<PhoneOff size={16} />}
            bg="#E94C57"
          />
          <PillButton
            data-testid={`${dataTestId}-accept`}
            onClick={onAccept}
            label="Answer"
            icon={<Phone size={16} />}
            bg="#35D178"
          />
        </div>
      </motion.div>
    ) : null}
  </AnimatePresence>
);

const PillButton = ({ 'data-testid': dataTestId, onClick, label, icon, bg }) => (
  <button
    type="button"
    data-testid={dataTestId}
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      height: 42,
      borderRadius: 999,
      background: bg,
      color: '#FFFFFF',
      border: 'none',
      cursor: 'pointer',
      fontFamily: "'Inria Serif', serif",
      fontSize: 15,
      fontWeight: 700,
      letterSpacing: '0.01em',
      boxShadow: `0 12px 24px ${bg}66`,
    }}
  >
    {icon}
    {label}
  </button>
);
